import { DeleteOutlined, DragOutlined } from '@ant-design/icons';
import { useRef } from 'react';
import styled from 'styled-components';

import { Divider, Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import Checkbox from '@/components/Checkbox/Checkbox';
import Typo from '@/components/Typography/Typo';
import type { Dashboard2FolderT } from '@/features/Dashboard/types/types';
import service from '@/services/index';
import useDropdownStore from '@/stores/pure/DropdownStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { useRecentDashboardHistory } from '@/stores/WorkspaceCacheStore';
import { radius } from '@/styles/sizes';

import { useInvalidateDashboardListQueries } from '../hooks/useDashboardListQuery';
import DashboardFolderSelectModal from './Modals/DashboardFolderSelectModal';

type Props = {
  selectedList: string[];
  onClearListClick: () => void;
  refetchList: () => void;
  folders?: Dashboard2FolderT[];
  currentFolder?: string;
};

export const DASHBOARD_SELECT_MODAL_KEY = 'DASHBOARD_SELECT_MODAL_KEY';

const DashboardFloatActionMenu = (props: Props) => {
  const setPopup = usePopup();
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const dashboardFolderSelectModalRef = useRef<HTMLButtonElement>(null);
  const addToast = useToast();
  const { deleteHistory } = useRecentDashboardHistory();
  const invalidateDashboardLists = useInvalidateDashboardListQueries();

  const deleteDashboards = async () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete dashboards',
      message: 'Are you sure you want to delete selected dashboards?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        await service.dashboard2.deleteDashboards(props.selectedList);
        deleteHistory(props.selectedList);
        await invalidateDashboardLists();
        addToast('Dashboard has been deleted', 'success');
        props.refetchList();
        props.onClearListClick();
      },
    });
  };

  const handleSelectDashboardFolder = async (folderAlias: string) => {
    try {
      if (folderAlias === 'all') {
        await service.dashboard2.moveDashboartToRoot(props.selectedList);
      } else {
        await service.dashboard2.moveDashboardToFolder(
          folderAlias,
          props.selectedList,
        );
      }
      await invalidateDashboardLists();
      addToast('Dashboard has been moved', 'success');
      props.refetchList();
      props.onClearListClick();
    } catch (e) {
      addToast('Error! try again', 'fail');
    }
  };

  return (
    <Style>
      <Checkbox
        halfCheck
        checked
        onChange={(e) => {
          props.onClearListClick();
        }}
      />
      <Typo>{props.selectedList.length} selected</Typo>
      <Divider direction="v" length={16} />
      <Flex w="wrap" f={1} j="center">
        <Flex>
          <Button
            ref={dashboardFolderSelectModalRef}
            leftIcon={<DragOutlined />}
            variant="ghost"
            sizeType="s"
            disabled={props.folders === undefined || props.folders.length === 0}
            onClick={() => {
              setCurrDropdown(DASHBOARD_SELECT_MODAL_KEY);
            }}
          >
            Move to
          </Button>
          <Button
            leftIcon={<DeleteOutlined />}
            variant="error_ghost"
            sizeType="s"
            onClick={deleteDashboards}
          >
            Delete
          </Button>
        </Flex>
      </Flex>
      <DashboardFolderSelectModal
        name={DASHBOARD_SELECT_MODAL_KEY}
        currentFolder={props.currentFolder}
        anchorRef={dashboardFolderSelectModalRef}
        onSelect={handleSelectDashboardFolder}
        modalOptions={{
          offset: {
            y: -40,
          },
        }}
        folders={props.folders}
      />
    </Style>
  );
};

const Style = styled(Flex)`
  position: absolute;
  width: fit-content;
  bottom: 24px;
  transform: translateX(-50%);
  align-items: center;
  background-color: ${(props) => props.theme.colors.gray100};
  box-shadow: 4px 8px 16px ${(props) => props.theme.colors.shadow};
  border: 1px solid ${(props) => props.theme.colors.primary100};
  border-radius: ${radius.card}px;
  padding: 12px;
  gap: 12px;
  & > * {
    flex-shrink: 0;
  }
`;

export default DashboardFloatActionMenu;
